/**
 * API client.
 *
 * The access token lives in memory only; the refresh token is an httpOnly
 * cookie the browser sends to /api/auth on its own. A 401 triggers one silent
 * refresh, and concurrent 401s share that single refresh instead of stampeding.
 *
 * Base URL: every path below is normally relative ('/api/...'), so the SPA
 * calls its own origin - true for the ordinary web deployment and for local
 * dev (vite.config.ts proxies /api to the API server). The bundled Android
 * build is the exception: it loads the SPA from a local virtual origin
 * (docs/android/architecture.md - WebViewAssetLoader), so relative paths
 * would resolve against that origin instead of the real API. VITE_API_BASE_URL
 * is baked in at build time for that one case (empty string elsewhere,
 * meaning "same origin, do nothing").
 */

import { deviceIdForAuth } from './deviceId';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

/**
 * Resolves a path such as '/api/menu' against the configured API origin.
 * Exported (not just used internally by `call()` below) so lib/deviceApi.ts
 * can resolve the same real API origin for its own, deliberately separate,
 * device-credential-authenticated calls (X-Device-Id/X-Device-Secret, never
 * this file's session cookie/bearer token - see deviceApi.ts's own file doc)
 * without a second copy of the VITE_API_BASE_URL logic to drift from this one.
 */
export const apiUrl = (path: string): string => `${API_BASE_URL}${path}`;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Array<{ path: string; message: string }>;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Array<{ path: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Field-level messages, keyed by form field name. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details ?? []) out[detail.path] = detail.message;
    return out;
  }
}

let accessToken: string | null = null;
let onUnauthenticated: ((reason?: string) => void) | null = null;
let refreshInFlight: Promise<RefreshOutcome> | null = null;
/**
 * Why the last refresh failed, when the server said something worth repeating.
 *
 * An expired session and a session signed out for sitting idle both end up
 * here, and the difference is the whole point of the second one: somebody who
 * is told "you were signed out for being idle" knows nothing is wrong.
 */
let lastRefreshFailure: string | undefined;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};
export const getAccessToken = (): string | null => accessToken;
export const setUnauthenticatedHandler = (handler: (reason?: string) => void): void => {
  onUnauthenticated = handler;
};

/**
 * Exchanges the refresh cookie for an access token.
 *
 * Three outcomes, not two: `true` (a fresh token), `false` (the server
 * refused the cookie - the session is over), and `'unknown'` (we could not
 * ask). Callers must not treat the third as a sign-out.
 */
export type RefreshOutcome = true | false | 'unknown';

async function refreshSession(): Promise<RefreshOutcome> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(apiUrl('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // deviceId lets a pinned kiosk terminal (Phase 6) keep resolving to
        // its own property on every silent refresh; undefined (every
        // ordinary browser tab) drops out of the JSON body entirely.
        body: JSON.stringify({ deviceId: deviceIdForAuth() }),
      });
      if (!res.ok) {
        const failure = (await res.json().catch(() => null)) as {
          error?: { code?: string };
        } | null;
        lastRefreshFailure = failure?.error?.code;
        /*
         * "Ask again later" is not "you are signed out".
         *
         * A 429, a gateway restarting mid-deploy or a 5xx all mean we could
         * not ask - the session may be perfectly good. Only the server
         * actually refusing the token (401/403) ends a session. Reporting
         * the rest as a sign-out is what put people on the login screen for
         * reloading a page too often.
         */
        return res.status === 401 || res.status === 403 ? false : 'unknown';
      }
      lastRefreshFailure = undefined;
      const body = (await res.json()) as { data: { accessToken: string } };
      accessToken = body.data.accessToken;
      return true;
    } catch {
      // Offline, DNS, CORS, connection refused: we never got an answer.
      lastRefreshFailure = 'NETWORK';
      return 'unknown';
    } finally {
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  raw?: boolean;
}

async function call<T>(path: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
  const { method = 'GET', body, signal, raw } = options;

  const res = await fetch(apiUrl(path), {
    method,
    credentials: 'include',
    signal,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 401 && !isRetry && !path.startsWith('/api/auth/')) {
    // Cloned before anything reads the body, so the reason survives whichever
    // of this request and the refresh noticed the sign-out first.
    const refused = (await res
      .clone()
      .json()
      .catch(() => null)) as { error?: { code?: string } } | null;

    const refreshed = await refreshSession();
    if (refreshed === true) return call<T>(path, options, true);
    // Only a refusal ends the session. When the refresh could not be made at
    // all, this request still failed - the caller sees the 401 - but the
    // session is left alone so the next attempt can recover it.
    if (refreshed === false) {
      accessToken = null;
      onUnauthenticated?.(refused?.error?.code ?? lastRefreshFailure);
    }
  }

  if (res.status === 204) return undefined as T;
  if (raw) return (await res.text()) as T;

  const payload = (await res.json().catch(() => null)) as {
    data?: T;
    error?: { code: string; message: string; details?: Array<{ path: string; message: string }> };
  } | null;

  if (!res.ok) {
    throw new ApiError(
      res.status,
      payload?.error?.code ?? 'UNKNOWN',
      payload?.error?.message ?? `Request failed (${res.status})`,
      payload?.error?.details,
    );
  }
  return (payload?.data ?? (payload as T)) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => call<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => call<T>(path, { method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) => call<T>(path, { method: 'PATCH', body: body ?? {} }),
  put: <T>(path: string, body?: unknown) => call<T>(path, { method: 'PUT', body: body ?? {} }),
  delete: <T>(path: string) => call<T>(path, { method: 'DELETE' }),
  refresh: refreshSession,
};

/** Streams a CSV/JSON export to the browser as a download. */
export async function download(path: string, filename: string): Promise<void> {
  const res = await fetch(apiUrl(path), {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, 'DOWNLOAD_FAILED', 'Could not generate that export');

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const qs = (
  params: Record<string, string | number | boolean | undefined | null>,
): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

/**
 * The field-by-field reasons behind a refused write.
 *
 * The API answers a validation failure with "Check the highlighted fields" and
 * a list of paths. Without this the message is a lie: nothing is highlighted,
 * and somebody who typed one character wrong in an IFSC has no way to find it.
 */
export function fieldErrors(caught: unknown): Record<string, string> {
  if (!(caught instanceof ApiError) || !Array.isArray(caught.details)) return {};
  const mapped: Record<string, string> = {};
  for (const detail of caught.details) {
    // The first reason for a field is the one worth showing.
    if (!(detail.path in mapped)) mapped[detail.path] = detail.message;
  }
  return mapped;
}
